import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  listMembers,
  updateMemberRole,
  removeMember,
  type Member,
  type OrgRole,
} from '../lib/api';
import { timeAgo } from '../lib/timeAgo';

const ROLE_LABELS: Record<OrgRole, string> = {
  owner:  'Owner',
  admin:  'Admin',
  member: 'Member',
};

export function MembersPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user) return;
    listMembers(user.orgId)
      .then((m) => { setMembers(m); setLoading(false); })
      .catch(() => { setError('Failed to load members.'); setLoading(false); });
  }, [user]);

  if (!user) return null;

  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    return !q || m.displayName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
  });

  async function handleRoleChange(memberId: string, role: OrgRole) {
    await updateMemberRole(user!.orgId, memberId, role);
    setMembers((prev) => prev.map((m) => m.id === memberId ? { ...m, role } : m));
  }

  async function handleRemove(memberId: string) {
    if (!confirm('Remove this member from the org?')) return;
    await removeMember(user!.orgId, memberId);
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Members</h1>
          <p className="page-subtitle">
            {members.length} member{members.length !== 1 ? 's' : ''} in {user.orgName}
          </p>
        </div>
        <button className="btn btn--primary">Invite member</button>
      </div>

      <div style={{ marginBottom: 'var(--space-4)' }}>
        <input
          type="search"
          className="form-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          style={{ maxWidth: '320px' }}
          aria-label="Search members"
        />
      </div>

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {!loading && !error && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Joined</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>
                    No members match your search.
                  </td>
                </tr>
              ) : (
                filtered.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 500 }}>{m.displayName}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>
                      {m.email}
                      {m.emailIsRelay && (
                        <span className="badge badge--neutral" style={{ marginLeft: 'var(--space-2)' }}>
                          Relay
                        </span>
                      )}
                    </td>
                    <td>
                      {user.role === 'owner' && m.id !== user.id ? (
                        <select
                          value={m.role}
                          onChange={(e) => handleRoleChange(m.id, e.target.value as OrgRole)}
                          className="form-input"
                          style={{ padding: '2px var(--space-2)', width: 'auto' }}
                          aria-label={`Role for ${m.displayName}`}
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                          <option value="owner">Owner</option>
                        </select>
                      ) : (
                        <span className="badge badge--neutral">{ROLE_LABELS[m.role]}</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge badge--${m.status === 'active' ? 'success' : m.status === 'pending' ? 'warning' : 'neutral'}`}
                      >
                        {m.status}
                      </span>
                    </td>
                    <td style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                      {timeAgo(m.joinedAt)}
                    </td>
                    <td>
                      {m.id !== user.id && (
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => handleRemove(m.id)}
                          style={{ color: 'var(--color-danger)' }}
                          aria-label={`Remove ${m.displayName}`}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
